// Aging, carrying capacity and pace (HER-5, HER-6, ARCH §28.8). PURE (ENG-1).
//
// Under the rule an adult of a table species dies of old age once its age reaches its
// genome's lifespan divided by the world's pace — unless it is named (a name tag exempts
// it for life), a baby (babies never age out), or ridden/leashed (E-54: deferred, never
// dumping a rider). Nothing here reads the rule itself: the entity system asks once per
// tick and passes the answer in, so a rule-off world does not even call this.
import type { Entity } from '../entities/entity';
import type { Pace } from './rule';

/** Why an animal is exempt from old age this tick, or null when it is not. */
export type AgeExemption = 'baby' | 'named' | 'no-lifespan' | 'held';

/** Is `e` due to die of old age at `pace` (§28.8)? `held` is the host's E-54 answer —
 *  ridden or leashed. Returns the exemption that spares it, or null when it is due. */
export function oldAgeCheck(e: Entity, pace: Pace, held: boolean): AgeExemption | 'young' | null {
  if (e.baby) return 'baby';
  if (e.name !== undefined) return 'named';
  if (e.traits.lifespanTicks <= 0) return 'no-lifespan';
  if (e.age < e.traits.lifespanTicks / pace) return 'young';
  return held ? 'held' : null;
}

/** A breeding timer under the rule's pace (§28.8): `ticks / pace`, at least 1. Only ever
 *  called with the rule on — the rule-off assignments keep their exact constants. */
export function paced(ticks: number, pace: Pace): number {
  return Math.max(1, Math.round(ticks / pace));
}
